import { SystemOptionType } from 'data/types';

export type CoverageShortfall = {
  key: string;
  text: string;
};

type CondenserCoverageType = SystemOptionType['condenserCoverage'][number];

export const INSUFFICIENT_COVERAGE_THRESHOLD_PERCENT = 95;

export function isInsufficientCoverage(
  percent: number | null,
  thresholdPercent: number = INSUFFICIENT_COVERAGE_THRESHOLD_PERCENT
): percent is number {
  return percent !== null && percent < thresholdPercent;
}

function getInsufficientTimePercent(
  condenser: CondenserCoverageType,
  type: 'heating' | 'cooling'
): number {
  return (
    (type === 'heating'
      ? condenser.percentageOfTimeOfInsufficientHeating
      : condenser.percentageOfTimeOfInsufficientCooling) ?? 0
  );
}

function getPartialCoveragePercent(
  condenser: CondenserCoverageType,
  type: 'heating' | 'cooling'
): number {
  return (
    (type === 'heating'
      ? condenser.avgPartialHeatingCoveragePercent
      : condenser.avgPartialCoolingCoveragePercent) ?? 0
  );
}

export function getCondenserShortfallFigures(
  condenser: CondenserCoverageType,
  type: 'heating' | 'cooling'
): { insufficientTimePercent: number; averagePartialCoveragePercent: number } {
  return {
    insufficientTimePercent: getInsufficientTimePercent(condenser, type),
    averagePartialCoveragePercent: Math.floor(
      getPartialCoveragePercent(condenser, type)
    )
  };
}

function describeShortfall(
  condenser: CondenserCoverageType,
  type: 'heating' | 'cooling',
  includeName: boolean
): string {
  const namePrefix = includeName ? `${condenser.name}: ` : '';
  const { insufficientTimePercent, averagePartialCoveragePercent } =
    getCondenserShortfallFigures(condenser, type);
  return `${namePrefix}insufficient ${insufficientTimePercent}% of the time, averaging ${averagePartialCoveragePercent}% coverage during that time`;
}

export function getCoverageShortfalls(
  type: 'heating' | 'cooling',
  score: number | null,
  condenserCoverage: SystemOptionType['condenserCoverage'] | undefined
): CoverageShortfall[] {
  if (score == null || score >= 100 || !condenserCoverage) {
    return [];
  }
  const includeName = condenserCoverage.length > 1;
  return condenserCoverage
    .filter((condenser) => getInsufficientTimePercent(condenser, type) > 0)
    .map((condenser) => ({
      key: condenser.uniqueId,
      text: describeShortfall(condenser, type, includeName)
    }));
}
